import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react';

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'loading'> {
  src: string;
  alt: string;
  /** Class applied to the wrapper div; useful when the wrapper provides sizing. */
  wrapperClassName?: string;
  'data-testid'?: string;
}

export function LazyImage({
  src,
  alt,
  className,
  wrapperClassName,
  'data-testid': testId,
  ...rest
}: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!wrapperRef.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsIntersecting(true);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setIsIntersecting(true);
          obs.disconnect();
          break;
        }
      }
    }, { rootMargin: '200px' });
    obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} data-testid={testId} className={wrapperClassName}>
      <img
        src={isIntersecting ? src : ''}
        alt={alt}
        loading="lazy"
        className={className}
        {...rest}
      />
    </div>
  );
}
